import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiService from '../services/api';
import { API_URL } from '../config/env';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useContext(AuthContext);

    // Sayfa yüklendiğinde kaydedilmiş bilgileri kontrol et
    useEffect(() => {
        const savedEmail = localStorage.getItem('rememberedEmail');
        const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
        
        if (savedRememberMe && savedEmail) {
            setEmail(savedEmail);
            setRememberMe(true);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setIsLoading(true);

        try {
            // API servisini kullanarak login isteği gönder
            const response = await apiService.auth.login({ email, password });
            const data = response.data;

            // Beni hatırla seçeneği işlemleri
            if (rememberMe) {
                localStorage.setItem('rememberedEmail', email);
                localStorage.setItem('rememberMe', 'true');
            } else {
                localStorage.removeItem('rememberedEmail');
                localStorage.removeItem('rememberMe');
            }

            // AuthContext üzerinden login işlemini gerçekleştir
            login(data.token, {
                id: data.id,
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                phone: data.phone,
                role: data.role,
            });

            // Yönlendirme
            const from = location.state?.from?.pathname || (data.role === 'admin' ? '/admin' : '/dashboard');
            navigate(from, { replace: true });
        } catch (error) {
            console.error('Hata:', error);
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message || 'Giriş başarısız!');
            } else {
                setErrorMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Sol Panel */}
            <div className="hidden lg:flex lg:w-2/5 bg-indigo-600 p-12 flex-col justify-between">
                <div>
                    <div className="flex items-center space-x-3">
                        <div className="bg-white rounded-full p-1">
                            <span className="text-indigo-600 text-xl font-bold">S</span>
                        </div>
                        <span className="text-white text-xl">Saticiyiz</span>
                    </div>
                    <div className="mt-20">
                        <h2 className="text-white text-4xl font-bold leading-tight">
                            Trendyol mağazanızı
                            <br />
                            daha etkili yönetin
                        </h2>
                        <p className="text-indigo-200 mt-4">
                            Satışlarınızı takip edin, stok yönetimini kolaylaştırın ve karlılığınızı artırın.
                        </p>
                    </div>
                </div>
                <div className="text-indigo-200">
                    © 2024 Saticiyiz. Tüm hakları saklıdır.
                </div>
            </div>

            {/* Sağ Panel - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="max-w-md w-full">
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-bold text-gray-900">Giriş Yapın</h1>
                        <p className="mt-3 text-gray-500">Hesabınıza giriş yaparak devam edin</p>
                        {/* Debug bilgisi - Production'da kaldırın */}
                        {process.env.NODE_ENV === 'development' && (
                            <p className="mt-1 text-xs text-gray-400">API URL: {API_URL}</p>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{errorMessage}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-posta adresi</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Şifre</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                                    Beni hatırla
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link 
                                    to="/forgot-password" 
                                    className="font-medium text-indigo-600 hover:text-indigo-500"
                                >
                                    Şifrenizi mi unuttunuz?
                                </Link>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                                    isLoading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Giriş yapılıyor...
                                    </>
                                ) : (
                                    'Giriş Yap'
                                )}
                            </button>
                        </div>
                    </form>

                    <p className="mt-6 text-center text-sm text-gray-500">
                        Hesabınız yok mu?{' '}
                        <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
                            Hemen oluşturun
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
